import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule, Optional, SkipSelf, InjectionToken } from '@angular/core';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ReplaySubject, Subject, from, throwError, of, Observable, forkJoin, map as map$1, catchError as catchError$1, take } from 'rxjs';
import { tap, first, finalize, map, catchError, switchMap, filter } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import * as i1$1 from '@angular/router';
import { NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { v4 } from 'uuid';
import * as i1$2 from '@elastic/apm-rum-angular';
import { ApmModule, ApmService } from '@elastic/apm-rum-angular';

var AuthEventType;
(function (AuthEventType) {
    AuthEventType["READY"] = "onReady";
    AuthEventType["AUTH_SUCCESS"] = "onAuthSuccess";
    AuthEventType["AUTH_ERROR"] = "onAuthError";
    AuthEventType["AUTH_REFRESH_SUCCESS"] = "onAuthRefreshSuccess";
    AuthEventType["AUTH_REFRESH_ERROR"] = "onAuthRefreshError";
    AuthEventType["AUTH_LOGOUT"] = "onAuthLogout";
    AuthEventType["AUTH_LOGOUT_ERROR"] = "onAuthLogoutError";
    AuthEventType["TOKEN_EXPIRED"] = "onTokenExpired";
})(AuthEventType || (AuthEventType = {}));
var AuthFlow;
(function (AuthFlow) {
    AuthFlow["IMPLICIT"] = "implicit";
    AuthFlow["STANDARD"] = "standard";
    AuthFlow["HYBRID"] = "hybrid";
})(AuthFlow || (AuthFlow = {}));
const urlDev$1 = 'https://api-manager.dev.universia.net/coreplatform-keycloak-token-mgmt/api/token/invalidate';
var InvalidateTokenUrl;
(function (InvalidateTokenUrl) {
    InvalidateTokenUrl["localhost"] = "https://api-manager.dev.universia.net/coreplatform-keycloak-token-mgmt/api/token/invalidate";
    InvalidateTokenUrl["dev"] = "https://api-manager.dev.universia.net/coreplatform-keycloak-token-mgmt/api/token/invalidate";
    InvalidateTokenUrl["pre"] = "https://api-manager.pre.universia.net/coreplatform-keycloak-token-mgmt/api/token/invalidate";
    InvalidateTokenUrl["pro"] = "https://api-manager.universia.net/coreplatform-keycloak-token-mgmt/api/token/invalidate";
})(InvalidateTokenUrl || (InvalidateTokenUrl = {}));

class AuthenticationStorageService {
    constructor() {
        this.storage = localStorage;
        this.storeName = 'AAS';
    }
    /**
     * Adds the name of the property inside the storage.
     *
     * @param value Storage propertyName
     */
    setStoreName(value) {
        this.storeName = value ? `${value}_AAS` : 'AAS';
    }
    /**
     * Gets the name of the property inside the storage.
     *
     * @param value Storage propertyName
     */
    getStoreName() {
        return this.storeName;
    }
    /**
     * Gets the authentication object that is kept in storage as long as it has not expired (the refresh token).
     *
     * @returns An AuthStoreExtended object or null.
     */
    getAuthStore() {
        const data = this.storage.getItem(this.storeName);
        if (data) {
            const storeObj = JSON.parse(data);
            if (!this.isTokenExpired(storeObj)) {
                return storeObj;
            }
            else {
                this.deleteAuthStore();
            }
        }
        return null;
    }
    /**
     * Create an authentication object in storage.
     *
     * @param item An AuthenticationStore object.
     */
    createAuthStore(item) {
        this.storage.setItem(this.storeName, JSON.stringify(item));
    }
    /**
     * If we have in storage the authentication object, updates it.
     *
     * @param item An AuthenticationStore object.
     */
    updateAuthStore(item) {
        const data = this.storage.getItem(this.storeName);
        if (data) {
            this.storage.setItem(this.storeName, JSON.stringify(item));
        }
    }
    /**
     * Removes the authentication object from storage.
     */
    deleteAuthStore() {
        this.storage.removeItem(this.storeName);
    }
    /**
     * Check if the refresh token has expired based on its expiration date.
     *
     * @param data An AuthStoreExtended object.
     * @returns True if token has expired.
     */
    isTokenExpired(data) {
        const now = new Date().getTime();
        return now >= data.refreshExpiresIn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const EXPIRATION_IN = 60;
// @dynamic
class AuthenticationService {
    constructor(httpService, document, storageService) {
        this.httpService = httpService;
        this.document = document;
        this.storageService = storageService;
        /**
         * Subject exposing keycloak events to listen to.
         */
        this.authEventsSubject = new ReplaySubject();
        /**
         * Subject to know when is updating token and finish to update.
         */
        this.refreshedTokenSubject = new Subject();
    }
    /**
     * Inits Keycloak with a configuration and creates the Kc events subscription.
     * Modifies the original configuration if the tokens property has a value. If it has no value, checks the auth storage to see if part of the configuration can be obtained from there.
     *
     * @param configuration Service configuration.
     * @param tokens Property to add the access and refresh tokens to the initialization of the Kc adapter.
     * @return An observable of boolean type or an error. True when the user is authenticated and false if is not authenticated.
     */
    init(configuration, tokens) {
        this.okInInit = true;
        this.config = configuration;
        this.storageService.setStoreName(this.config.config.realm);
        const configCopy = this.getConfigToUse(tokens);
        const { init, config } = configCopy;
        return this.initKcInstance(config, init);
    }
    /**
     * Inits Keycloak. If the initialization went well, sets the access and refresh token values in the auth store when the user is authenticated.
     *
     * @param config Keycloak config.
     * @param initConfig Keycloak init config.
     * @returns An observable of boolean type or an error. True when the user is authenticated and false if is not authenticated.
     */
    initKcInstance(config, initConfig) {
        try {
            this.authInstance = new Keycloak(config);
            this.manageAuthEvents();
            return from(this.authInstance.init(initConfig)).pipe(tap((result) => {
                if (result) {
                    this.createAuthStore();
                }
            }));
        }
        catch (err) {
            this.okInInit = false;
            return throwError(() => ({ type: AuthEventType.AUTH_ERROR, data: err }));
        }
    }
    /**
     * Updates the token if it is expired. If part of the configuration object is in auth storage, modify its value.
     *
     * @param expirationIn Number is seconds. It´s used to know if the expired time of the token is between 0 and this number. It´s optional.
     * @return An observable of boolean type or an error. True when the user is authenticated and false if is not authenticated.
     */
    updateToken(expirationIn = EXPIRATION_IN) {
        if (this.config.init?.flow === AuthFlow.HYBRID || this.config.init?.flow === AuthFlow.IMPLICIT) {
            return this.tokenExpiredIn(expirationIn) ? this.init(this.config) : of(true);
        }
        else {
            return from(this.authInstance.updateToken(expirationIn)).pipe(tap((refreshed) => {
                if (refreshed) {
                    this.updateAuthStore();
                }
            }));
        }
    }
    /**
     * Gets the user token.
     *
     * @param expirationIn Number is seconds. It´s used to know if the expired time of the token is between 0 and this number. It´s optional.
     * @return An observable with the token.
     */
    getToken(expirationIn = EXPIRATION_IN) {
        if (!this.authInstance) {
            return of(null);
        }
        else if (!this.isAuthenticated()) {
            this.manageOnAuthLogout();
            return of(null);
        }
        else {
            return this.initUpdateToken(expirationIn).pipe(first(), finalize(() => this.refreshedToken()), map(() => {
                if (!this.authInstance.token) {
                    this.manageOnAuthLogout();
                }
                return this.authInstance.token || null;
            }), catchError(() => of(null)));
        }
    }
    /**
     * Creates a history url with the actual url and it sends us to the Kc login.
     *
     * @param options Login options. If this parameter exists, it uses this configuration instead of the initial one.
     */
    login(options) {
        window.history.pushState(null, this.document.title, this.document.location.href);
        const config = options ? options : this.config?.login;
        this.authInstance.login(config);
    }
    /**
     * Disables the actual token an it throw the Kc logout. If part of the configuration is in storage, remove its value.
     *
     * @param options Logout options. If this parameter exists, it uses this configuration instead of the initial one.
     * @return An observable of boolean type. True if logout is success, false if the logout is not executed.
     */
    logout(options) {
        const config = options ? options : this.config?.logout;
        this.storageService.deleteAuthStore();
        if (!this.authInstance || !this.authInstance.authenticated) {
            return of(false);
        }
        else {
            const url = this.config['env'] ? InvalidateTokenUrl[this.config['env']] : InvalidateTokenUrl.pro;
            return this.httpService.post(url, {}).pipe(map(() => {
                this.authInstance.logout(config);
                return true;
            }), catchError(() => {
                this.authInstance.logout(config);
                return of(true);
            }));
        }
    }
    /**
     * Creates a history url with the actual url and it sends us to the Kc register.
     *
     * @param options Login options. If this parameter exists, it uses this configuration instead of the initial one.
     */
    register(options) {
        const config = options ? options : this.config?.login;
        config['action'] = 'register';
        window.history.pushState(null, this.document.title, this.document.location.href);
        this.authInstance.register(config);
    }
    /**
     * Return an observable to listen all Kc and service events.
     *
     * @return An observable.
     */
    getAuthEvents() {
        return this.authEventsSubject.asObservable();
    }
    /**
     * Indicates if the user is authenticated.
     *
     * @return True if user is authenticated, false otherwise.
     */
    isAuthenticated() {
        return this.authInstance?.authenticated || false;
    }
    /**
     * Returns the created Kc instance.
     */
    getInstance() {
        return this.authInstance;
    }
    /**
     * Returns the parsed token.
     *
     * @return An object with token data.
     */
    getTokenParsed() {
        return this.authInstance?.tokenParsed || null;
    }
    /**
     * Returns the refresh token.
     *
     * @return Refresh token.
     */
    getRefreshToken() {
        return this.authInstance?.refreshToken || null;
    }
    /**
     * Returns the refresh token.
     *
     * @return An object with refresh token data.
     */
    getRefreshTokenParsed() {
        return this.authInstance?.refreshTokenParsed || null;
    }
    /**
     * Indicates if token expires in a determinate time.
     *
     * @param expirationIn Number is seconds. It´s used to know if the expired time of the token is between 0 and this number. It´s optional.
     * @return True if token expires in this time.
     */
    tokenExpiredIn(expirationIn = 0) {
        return this.authInstance?.isTokenExpired(expirationIn) || false;
    }
    /**
     * Clear the actual token.
     */
    clearToken() {
        this.authInstance?.clearToken();
    }
    /**
     * Indicates if Kc is initializated.
     *
     * @return True if Kc is initializated.
     */
    isInitializationOK() {
        return this.okInInit;
    }
    /**
     * Returns roles in realm.
     *
     * @return String[] with all roles have realm.
     */
    getRealmRoles() {
        return this.authInstance?.realmAccess?.roles || [];
    }
    /**
     * Returns roles in resource.
     *
     * @return String[] with all roles have resource.
     */
    getResourceRoles(clientId) {
        return this.authInstance?.resourceAccess?.[clientId]?.roles || [];
    }
    /**
     * Indicates if the token has the given realm role.
     *
     * @param role Role to check. It´s mandatory.
     *
     * @return True if the token has the given realm role.
     */
    hasRealmRole(role) {
        return this.authInstance?.hasRealmRole(role) || false;
    }
    /**
     * Indicates if the token has the given role for the resource or clientId.
     *
     * @param role Role to check. It´s mandatory.
     * @param resource Resource where find. It's optional, if not specified clientId is used.
     *
     * @return True if the token has the given role for the resource or clientId.
     */
    hasResourceRole(role, resource) {
        return this.authInstance?.hasResourceRole(role, resource) || false;
    }
    /**
     * Returns the initial Keycloak configuration object.
     *
     * @returns An AuthenticationConfig object.
     */
    getConfig() {
        return this.config;
    }
    /**
     * Sends an event via authEventsSubject.
     *
     * @param type Event type.
     * @param data Data to send.
     */
    sendEvent(type, data) {
        const eventObj = { type };
        if (data) {
            eventObj['data'] = data;
        }
        this.authEventsSubject.next(eventObj);
    }
    /**
     * When a Kc event is listen, it send a service event via authEventSubject.
     */
    manageAuthEvents() {
        this.authInstance.onReady = (authenticated) => {
            this.sendEvent(AuthEventType.READY, authenticated);
        };
        this.authInstance.onAuthSuccess = () => {
            this.sendEvent(AuthEventType.AUTH_SUCCESS);
        };
        this.authInstance.onAuthError = (error) => {
            this.sendEvent(AuthEventType.AUTH_ERROR, error);
        };
        this.authInstance.onAuthRefreshSuccess = () => {
            this.sendEvent(AuthEventType.AUTH_REFRESH_SUCCESS);
        };
        this.authInstance.onAuthRefreshError = () => {
            this.sendEvent(AuthEventType.AUTH_REFRESH_ERROR);
        };
        this.authInstance.onAuthLogout = () => {
            this.manageOnAuthLogout();
        };
        this.authInstance.onTokenExpired = () => {
            this.sendEvent(AuthEventType.TOKEN_EXPIRED);
        };
    }
    /**
     * Send AUTH_LOGOUT event and delete the aout store.
     */
    manageOnAuthLogout() {
        this.storageService.deleteAuthStore();
        this.sendEvent(AuthEventType.AUTH_LOGOUT);
    }
    /**
     * Indicates that the update process is complete.
     */
    refreshedToken() {
        this.updatingToken = false;
        this.refreshedTokenSubject.next();
    }
    /**
     * Manages the request wait. When a request is released, run the update process again.
     *
     * @param expirationIn Number is seconds. It´s used to know if the expired time of the token is between 0 and this number. It´s optional.
     */
    waitForUpdateToken(expirationIn = EXPIRATION_IN) {
        return this.refreshedTokenSubject.asObservable().pipe(first(), switchMap(() => this.initUpdateToken(expirationIn)));
    }
    /**
     * Manages the token update process. If a request occurs when another is already in process, wait until the running request is finished.
     *
     * @param expirationIn Number is seconds. It´s used to know if the expired time of the token is between 0 and this number. It´s optional.
     */
    initUpdateToken(expirationIn = EXPIRATION_IN) {
        if (!this.updatingToken) {
            this.updatingToken = true;
            return this.updateToken(expirationIn);
        }
        else {
            return this.waitForUpdateToken(expirationIn);
        }
    }
    /**
     * Return a config object for Keycloak based on:
     *   - return original if we have no initialization tokens or object in auth storage.
     *   - return modified config object if there is initialization tokens.
     *   - return modified config object if there is a config object in the auth storage.
     *
     * @param tokens An AuthenticationStoreConfig object. It has the access and refresh tokens.
     * @returns An AuthenticationConfig object.
     */
    getConfigToUse(tokens = null) {
        return tokens ? this.setConfigToUseTokens(tokens) : this.setConfigToUseTokens(this.storageService.getAuthStore());
    }
    /**
     * Modifies the original configuration object with the new token-based configuration. Returns a new configuration object.
     *
     * @param tokens An AuthenticationStoreConfig or AuthenticationStore object. It has the access and refresh tokens.
     * @returns A new AuthenticationConfig object
     */
    setConfigToUseTokens(tokens) {
        const newConfig = JSON.parse(JSON.stringify(this.config));
        if (tokens) {
            delete newConfig.init.onLoad;
            newConfig.init.token = tokens.accessToken;
            newConfig.init.scope = 'openid profile';
            newConfig.init.refreshToken = tokens.refreshToken;
        }
        return newConfig;
    }
    /**
     * Creates an auth store object in the auth storage.
     */
    createAuthStore() {
        const authStore = this.createAuthStoreObj();
        this.storageService.createAuthStore(authStore);
    }
    /**
     * Modifies an auth store object in the auth storage.
     */
    updateAuthStore() {
        const authStore = this.createAuthStoreObj();
        this.storageService.updateAuthStore(authStore);
    }
    /**
     * Create the object needed by the Storage service to add or modify it in Storage.
     *
     * @returns An AuthenticationStore object.
     */
    createAuthStoreObj() {
        return {
            accessToken: this.authInstance.token || '',
            refreshToken: this.getRefreshToken(),
            expiresIn: (this.authInstance.tokenParsed?.exp || 0) * 1000,
            refreshExpiresIn: (this.authInstance.refreshTokenParsed?.exp || 0) * 1000
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationService, deps: [{ token: i1.HttpClient }, { token: DOCUMENT }, { token: AuthenticationStorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: AuthenticationStorageService }]; } });

class AuthenticationInterceptorService {
    constructor(authenticationService) {
        this.authenticationService = authenticationService;
    }
    intercept(request, next) {
        if (request.headers.get('no-authorization')) {
            request = this.removeNoAuthHeader(request);
            return next.handle(request);
        }
        else if (request.headers.get('Authorization')) {
            return next.handle(request);
        }
        else {
            return this.authenticationService.getToken().pipe(switchMap(token => {
                if (token) {
                    request = this.addAuthHeader(request, token);
                }
                return next.handle(request);
            }));
        }
    }
    removeNoAuthHeader(request) {
        const headers = request.headers.delete('no-authorization');
        return request.clone({ headers });
    }
    addAuthHeader(request, token) {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationInterceptorService, deps: [{ token: AuthenticationService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationInterceptorService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationInterceptorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AuthenticationService }]; } });

class AuthenticationModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('AuthenticationModule is already loaded. Import it in the AppModule only');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationModule, deps: [{ token: AuthenticationModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationModule, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationModule, providers: [
            AuthenticationService,
            AuthenticationStorageService,
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AuthenticationInterceptorService,
                multi: true
            }
        ], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule],
                    providers: [
                        AuthenticationService,
                        AuthenticationStorageService,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: AuthenticationInterceptorService,
                            multi: true
                        }
                    ]
                }]
        }], ctorParameters: function () { return [{ type: AuthenticationModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/**
 * @deprecated since version 6.0.0
 */
class AuthenticationGuard {
    constructor(authService) {
        this.authService = authService;
    }
    canActivate(route, state) {
        if (this.authService.isInitializationOK()) {
            return this.authService.isAuthenticated() ? true : this.manageError(route, state);
        }
        else {
            return this.manageError(route, state);
        }
    }
    canLoad(route, segments) {
        if (this.authService.isInitializationOK()) {
            return this.authService.isAuthenticated() ? true : this.manageError(route, segments);
        }
        else {
            return this.manageError(route, segments);
        }
    }
}

var Environment;
(function (Environment) {
    Environment["LOCALHOST"] = "localhost";
    Environment["DEV"] = "dev";
    Environment["PRE"] = "pre";
    Environment["PRO"] = "pro";
})(Environment || (Environment = {}));

class EnvironmentService {
    constructor(env) {
        if (env instanceof Observable) {
            env.subscribe((result) => {
                this.setEnvironment(result);
            });
        }
        else {
            this.setEnvironment(env);
        }
    }
    getEnvironment() {
        return this.environment;
    }
    setEnvironment(env) {
        if (Object.values(Environment).includes(env)) {
            this.environment = env;
        }
        else {
            throw new Error('EnvironmentService: The environment has to have one of the following values: dev, pre, pro');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EnvironmentService, deps: [{ token: 'ENVIRONMENT' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EnvironmentService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EnvironmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['ENVIRONMENT']
                }] }]; } });

class RouterEventsService {
    constructor(router) {
        this.router = router;
        this.startNavigationSubject = new Subject();
        this.endNavigationSubject = new Subject();
        this.initRouterEvents();
    }
    initChangeRoute() {
        return this.startNavigationSubject.asObservable();
    }
    executeInitChangeRoute() {
        this.startNavigationSubject.next();
    }
    endChangeRoute() {
        return this.endNavigationSubject.asObservable();
    }
    executeEndChangeRoute(event) {
        this.endNavigationSubject.next(event);
    }
    getPreviousUrl() {
        return this.previousUrl;
    }
    getCurrentUrl() {
        return this.currentUrl;
    }
    initRouterEvents() {
        this.router.events
            .pipe(filter((evt) => evt instanceof NavigationStart ||
            evt instanceof NavigationEnd ||
            evt instanceof NavigationCancel ||
            evt instanceof NavigationError))
            .subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.previousUrl = this.currentUrl;
                this.currentUrl = event.url;
            }
            if (event instanceof NavigationStart) {
                this.executeInitChangeRoute();
            }
            if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
                this.executeEndChangeRoute(event);
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RouterEventsService, deps: [{ token: i1$1.Router }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RouterEventsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RouterEventsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Router }]; } });

const GTM_KEY = new InjectionToken('GTM_KEY');
class AnalyticsService {
    constructor(gtmKey, document) {
        this.gtmKey = gtmKey;
        this.document = document;
        this.init();
    }
    /**
     * Method that inits GTM script.
     */
    init() {
        if (this.gtmKey) {
            const dataLayer = this.getDataLayer();
            if (!this.isGTMInstalled(dataLayer)) {
                dataLayer.push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js'
                });
                const scriptElement = this.document.createElement('script');
                scriptElement.async = true;
                scriptElement.src = 'https://www.googletagmanager.com/gtm.js?id=' + this.gtmKey;
                this.document.head.insertBefore(scriptElement, this.document.head.firstChild);
            }
        }
        else {
            console.warn('It is necessary to add the Gtm Key for analytics as a provider to be able to add the Gtm script');
        }
    }
    /**
     * Method to send GTM events.
     *
     * @param info Object with data to send.
     */
    sendGTMEvent(info) {
        try {
            const datalayer = window.dataLayer;
            datalayer.push(info);
        }
        catch (err) {
            if (!this.gtmKey) {
                throw new Error(`Error: no GTM key provided`);
            }
            else {
                throw new Error(`Error sending to GTM: ${JSON.stringify(err)}`);
            }
        }
    }
    getDataLayer() {
        window.dataLayer = window.dataLayer || [];
        return window.dataLayer;
    }
    isGTMInstalled(datalayer) {
        return datalayer.length > 0 && datalayer.find((item) => item['gtm.start'] || item['event'] === 'gtm.js');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnalyticsService, deps: [{ token: GTM_KEY }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnalyticsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [GTM_KEY]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class ConfigService {
    constructor(http, configFiles) {
        this.http = http;
        this.asyncConfig = new ReplaySubject();
        this.config$ = this.asyncConfig.asObservable();
        this.configFiles = configFiles;
    }
    getConfigFiles() {
        const calls = this.configFiles.map(url => this.createCall(url));
        return forkJoin(calls).pipe(map$1((configsData) => {
            this.config = configsData.reduce((acc, configData) => {
                return { ...acc, ...configData };
            }, {});
            this.asyncConfig.next(this.config);
            return true;
        }), catchError$1((err) => {
            this.asyncConfig.next(null);
            return throwError(() => err);
        }));
    }
    getConfig() {
        return this.config || null;
    }
    getAsyncConfig() {
        return this.config ? of(this.config) : this.config$;
    }
    getEnv() {
        return this.config?.env || null;
    }
    createCall(url) {
        return this.http.get(url).pipe(take(1));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ConfigService, deps: [{ token: i1.HttpClient }, { token: 'CONFIG_FILES' }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: Array, decorators: [{
                    type: Inject,
                    args: ['CONFIG_FILES']
                }] }]; } });

/**
 * Provides encoding and decoding of URL parameter and query-string values.
 *
 * Serializes and parses URL parameter keys and values to encode and decode them.
 * If you pass URL query parameters without encoding,
 * the query parameters can be misinterpreted at the receiving end.
 *
 * This a custom modification to the original `HttpUrlEncodingCodec` from Angular
 * (see original: https://github.com/angular/angular/blob/master/packages/common/http/src/params.ts#L34),
 * It has been modified to allow '+' character to be encoded in the queryParams by default.
 * In addition to its original functionality, you can set a custom dictionary of special characters to be
 * un-encoded after enconding.
 */
class UrlParamCodec {
    /**
     * Creates a new encoder for queryParams. A custom mapping can be passed to override the default one.
     *
     * @param replacementMap The custom character replacement mapping.
     */
    constructor(replacementMap) {
        this.replacements = STANDARD_ENCODING_REPLACEMENTS;
        if (replacementMap) {
            this.replacements = replacementMap;
        }
    }
    /**
     * Encodes a key name for a URL parameter or query-string.
     *
     * @param key The key name.
     * @returns The encoded key name.
     */
    encodeKey(key) {
        return standardEncoding(key, this.replacements);
    }
    /**
     * Encodes the value of a URL parameter or query-string.
     *
     * @param value The value.
     * @returns The encoded value.
     */
    encodeValue(value) {
        return standardEncoding(value, this.replacements);
    }
    /**
     * Decodes an encoded URL parameter or query-string key.
     *
     * @param key The encoded key name.
     * @returns The decoded key name.
     */
    decodeKey(key) {
        return decodeURIComponent(key);
    }
    /**
     * Decodes an encoded URL parameter or query-string value.
     *
     * @param value The encoded value.
     * @returns The decoded value.
     */
    decodeValue(value) {
        return decodeURIComponent(value);
    }
}
// Original Angular encoding replacements, these characters get un-encoded AFTER encoding the string
const STANDARD_ENCODING_REGEX = /%(\d[a-f0-9])/gi;
const STANDARD_ENCODING_REPLACEMENTS = {
    '40': '@',
    '3A': ':',
    '24': '$',
    '2C': ',',
    '3B': ';',
    // '2B': '+', // commented because it breaks searching for emails with valid '+' character on them
    '3D': '=',
    '3F': '?',
    '2F': '/'
};
const standardEncoding = (v, replacements) => {
    return encodeURIComponent(v).replace(STANDARD_ENCODING_REGEX, (s, t) => replacements[t] ?? s);
};

class UuidGenerator {
    /**
     * Generate an uuid v4 identifier.
     *
     * @returns The generated identifier.
     */
    static generate() {
        return v4();
    }
}

const tracingDev = 'https://api-manager.dev.universia.net';
var TracingOrigin;
(function (TracingOrigin) {
    TracingOrigin["localhost"] = "https://api-manager.dev.universia.net";
    TracingOrigin["dev"] = "https://api-manager.dev.universia.net";
    TracingOrigin["pre"] = "https://api-manager.pre.universia.net";
    TracingOrigin["pro"] = "https://api-manager.universia.net";
})(TracingOrigin || (TracingOrigin = {}));
const urlDev = 'https://apm.dev.universia.net';
var MonitoringUrl;
(function (MonitoringUrl) {
    MonitoringUrl["localhost"] = "https://apm.dev.universia.net";
    MonitoringUrl["dev"] = "https://apm.dev.universia.net";
    MonitoringUrl["pre"] = "https://apm.pre.universia.net";
    MonitoringUrl["pro"] = "https://apm.universia.net";
})(MonitoringUrl || (MonitoringUrl = {}));

class WebMonitoringService {
    constructor(apmService, authService, monitoringConfig) {
        this.apmService = apmService;
        this.authService = authService;
        if (monitoringConfig) {
            this.init(monitoringConfig);
        }
    }
    /**
     * Init the monitoring.
     *
     * @param config Monitoring config.
     */
    init(config) {
        this.config = JSON.parse(JSON.stringify(config));
        this.monitoringApp = this.initApm();
        this.listenUserAuthSuccess();
    }
    /**
     * Given an error, if apm is active, add an error uuid to it and send it to apm. Returns the generated uuid.
     *
     * @param error Js error.
     * @returns An uuid error or empty string if the monitoring app is not active.
     */
    captureError(error) {
        if (this.config.active) {
            const errorUuid = UuidGenerator.generate();
            this.setCustomContext({ errorUuid });
            this.addLabels({ errorUuid });
            this.monitoringApp?.captureError(error.originalError || error);
            return errorUuid;
        }
        return '';
    }
    /**
     * Given a http error, if apm is active, send it to apm.
     *
     * @param error Http error.
     */
    captureHttpError(error) {
        if (this.config.active) {
            const errorUuid = error?.error?.reqUuid || '';
            this.setCustomContext({
                errorUuid,
                httpErrorTrace: JSON.stringify(error?.error || '')
            });
            this.addLabels({ errorUuid });
            this.monitoringApp?.captureError(error);
        }
    }
    /**
     * Function to modify the context of an error message.
     *
     * @param context Apm context.
     */
    setCustomContext(context) {
        this.monitoringApp?.setCustomContext(context);
    }
    /**
     * Function to modify the labels of an error message.
     *
     * @param labels Apm labels.
     */
    addLabels(labels) {
        this.monitoringApp?.addLabels(labels);
    }
    /**
     * Initialize the Apm with the configuration passed to the service as a parameter and a changes to the serverUrl
     * and distributedTracingOrigins properties.
     *
     * @returns The instance of the Apm.
     */
    initApm() {
        this.config['serverUrl'] = MonitoringUrl[this.config.environment];
        this.config['distributedTracingOrigins'] = this.setTracingOrigins(this.config['distributedTracingOrigins']);
        return this.apmService.init(this.config);
    }
    /**
     * Add a default origin to the origins array that will have all the apps in the ecosystem based on the environment.
     *
     * @param origins Origins array.
     * @returns The origins array with the default origin.
     */
    setTracingOrigins(origins = []) {
        const originsCopy = [...origins];
        originsCopy.push(TracingOrigin[this.config.environment]);
        return originsCopy;
    }
    /**
     * Function that listens for authentication events to obtain the user's identifier when authenticated
     * and change the user's context in the Apm.
     */
    listenUserAuthSuccess() {
        this.authService.getAuthEvents().subscribe((event) => {
            if (event.type === AuthEventType.AUTH_SUCCESS) {
                const userToken = this.authService.getTokenParsed();
                this.changeUserContext(userToken);
            }
            if (event.type === AuthEventType.AUTH_LOGOUT) {
                this.changeUserContext();
            }
        });
    }
    /**
     * Change the user's context in the APM.
     *
     * @param parsedUserToken User token parsed object.
     */
    changeUserContext(parsedUserToken) {
        if (parsedUserToken) {
            this.monitoringApp.setUserContext({ id: parsedUserToken.sub });
        }
        else {
            this.monitoringApp.setUserContext({ id: '' });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringService, deps: [{ token: i1$2.ApmService }, { token: AuthenticationService }, { token: 'MONITORING_CONFIG', optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$2.ApmService }, { type: AuthenticationService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['MONITORING_CONFIG']
                }, {
                    type: Optional
                }] }]; } });

class WebMonitoringInterceptorService {
    constructor(monitoringService) {
        this.monitoringService = monitoringService;
    }
    intercept(request, next) {
        return next.handle(request).pipe(catchError$1((error) => {
            this.monitoringService.captureHttpError(error);
            return throwError(() => error);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringInterceptorService, deps: [{ token: WebMonitoringService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringInterceptorService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringInterceptorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: WebMonitoringService }]; } });

class WebMonitoringErrorHandlerService {
    constructor(monitoringService) {
        this.monitoringService = monitoringService;
    }
    handleError(error) {
        if (error instanceof EvalError ||
            error instanceof RangeError ||
            error instanceof ReferenceError ||
            error instanceof SyntaxError ||
            error instanceof TypeError ||
            error instanceof URIError ||
            error instanceof Error) {
            const errorUuid = this.monitoringService.captureError(error);
            this.showError(error, errorUuid);
        }
    }
    showError(error, errorUuid) {
        console.error(`errorUuid: ${errorUuid}: `, error);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringErrorHandlerService, deps: [{ token: WebMonitoringService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringErrorHandlerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringErrorHandlerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: WebMonitoringService, decorators: [{
                    type: Inject,
                    args: [WebMonitoringService]
                }] }]; } });

class WebMonitoringModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('WebMonitoringModule is already loaded. Import it in the AppModule only');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringModule, deps: [{ token: WebMonitoringModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringModule, imports: [ApmModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringModule, providers: [ApmService], imports: [ApmModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WebMonitoringModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ApmModule],
                    providers: [ApmService]
                }]
        }], ctorParameters: function () { return [{ type: WebMonitoringModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/*
 * Public API Surface of core
 */
/**
 * Authentication module
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsService, AuthEventType, AuthFlow, AuthenticationGuard, AuthenticationInterceptorService, AuthenticationModule, AuthenticationService, AuthenticationStorageService, ConfigService, Environment, EnvironmentService, GTM_KEY, InvalidateTokenUrl, MonitoringUrl, RouterEventsService, TracingOrigin, UrlParamCodec, UuidGenerator, WebMonitoringErrorHandlerService, WebMonitoringInterceptorService, WebMonitoringModule, WebMonitoringService };
